import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'

@customElement('cairn-splash')
export class CairnSplash extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100vh;
      background: var(--cairn-background-color, #f8fafc);
      position: fixed;
      top: 0;
      left: 0;
      z-index: 9999;
      transition: opacity 0.5s ease-out;
    }

    :host([hidden]) {
      opacity: 0;
      pointer-events: none;
    }

    .logo-container {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 132px;
      height: 132px;
      margin-bottom: 32px;
    }

    .logo {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      object-fit: cover;
      z-index: 2;
    }

    .progress-ring {
      position: absolute;
      top: 0;
      left: 0;
      width: 132px;
      height: 132px;
      transform: rotate(-90deg); /* Start progress at top */
    }

    .progress-circle {
      fill: none;
      stroke: var(--cairn-border-color, #e2e8f0);
      stroke-width: 6;
      opacity: 0.3;
    }

    .progress-circle-active {
      fill: none;
      stroke: var(--cairn-primary-color, #2d3748);
      stroke-width: 6;
      stroke-linecap: round;
      stroke-dasharray: 358.14; /* 2π × (120/2 - 6/2) = 2π × 57 */
      stroke-dashoffset: 358.14; /* Start with no progress */
      transition: stroke-dashoffset 0.3s ease-out;
      filter: drop-shadow(0 0 3px rgba(45, 55, 72, 0.3));
    }

    .app-name {
      font-size: var(--cairn-text-size-xl, 24px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin-top: 16px;
      letter-spacing: 0.5px;
    }

    .tagline {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
      margin-top: 8px;
      text-align: center;
    }

    @keyframes fadeOut {
      from {
        opacity: 1;
      }
      to {
        opacity: 0;
      }
    }

    .fade-out {
      animation: fadeOut 0.5s ease-out forwards;
    }
  `

  @property({ type: Number })
  progress = 0 // Progress from 0 to 100

  @property({ type: Boolean })
  fadeOut = false

  @property({ type: String })
  logoSrc = '/images/cairn-icon.png'

  @property({ type: String })
  statusMessage = 'Initializing Cairn...'

  override firstUpdated(): void {
    this.updateProgress() // Ensure initial progress is set correctly
  }

  override updated(changedProperties: Map<string, unknown>): void {
    if (changedProperties.has('progress')) {
      this.updateProgress()
    }
  }

  private updateProgress(): void {
    const circle = this.shadowRoot?.querySelector(
      '.progress-circle-active'
    ) as SVGCircleElement | null
    if (circle) {
      const circumference = 358.14 // 2π × radius (57px for stroke-width 6)
      const offset = circumference - (this.progress / 100) * circumference
      circle.style.strokeDashoffset = offset.toString()
    }
  }

  public hide(): void {
    this.fadeOut = true
    setTimeout((): void => {
      this.dispatchEvent(new CustomEvent('splash-hidden', { bubbles: true }))
    }, 500)
  }

  override render(): unknown {
    return html`
      <div class="logo-container">
        <!-- Circular progress ring -->
        <svg class="progress-ring" viewBox="0 0 132 132">
          <!-- Background circle -->
          <circle class="progress-circle" cx="66" cy="66" r="57"></circle>
          <!-- Progress circle -->
          <circle class="progress-circle-active" cx="66" cy="66" r="57"></circle>
        </svg>

        <!-- Logo in center -->
        <img class="logo" src="${this.logoSrc}" alt="Cairn" />
      </div>

      <div class="app-name">Cairn</div>
      <div class="tagline">${this.statusMessage}</div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cairn-splash': CairnSplash
  }
}
